import { useEffect, useRef, useState } from 'react';

import { useRedemptionState } from '@/features/code-redemption';
import { ApiError } from '@/shared/lib/apiClient';
import { useToastStore } from '@/shared/ui';

import { useParticipant } from '../hooks/useParticipant';

import styles from './ParticipantAutoIssue.module.css';
import { ParticipantOnboarding } from './ParticipantOnboarding';

/**
 * 페이지 진입 시 참여자 코드를 자동으로 발급해 주는 인라인 위젯.
 *
 * 동작:
 * - 마운트 시 `state.userCode === null`이면 즉시 서버에 발급 요청.
 *   React Strict Mode의 이중 마운트로 인한 중복 발급은 `useRef` 가드로 차단한다.
 * - 발급 진행 / 성공 / 실패 3가지 상태에 맞는 작은 인라인 UI를 노출한다.
 * - "이미 코드가 있나요? 이어서 하기" 링크를 항상 함께 두어, 다른 디바이스에서
 *   받은 코드로 진행 상태를 가져올 수 있게 한다(`<dialog>` 모달).
 *
 * 디자인:
 * - 게이트 형태가 아니라 **인라인 한 줄**로 표시되어 다른 섹션(일러스트·코드 입력·
 *   타임라인)이 같은 화면에 함께 보이도록 페이지 공간을 거의 차지하지 않는다.
 */
export function ParticipantAutoIssue(): React.JSX.Element {
  const { state } = useRedemptionState();
  const { issue } = useParticipant();
  const pushToast = useToastStore((s) => s.push);
  const triggeredRef = useRef(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  // mutation 객체 자체는 매 렌더마다 새 인스턴스일 수 있으므로,
  // 함수 참조만 deps에 두어 자동 발급 effect의 안정성을 보장한다.
  const issueAsync = issue.mutateAsync;

  // 자동 발급 — 코드가 없을 때 1회만 트리거.
  useEffect(() => {
    if (triggeredRef.current) {
      return;
    }
    if (state.userCode !== null) {
      return;
    }
    triggeredRef.current = true;
    issueAsync().catch((error: unknown) => {
      // 실패 시 다시 시도할 수 있도록 가드를 풀어준다.
      triggeredRef.current = false;
      const message =
        error instanceof ApiError
          ? error.message
          : '코드 발급 중 오류가 발생했어요. 잠시 후 다시 시도해 주세요.';
      pushToast({ variant: 'error', message });
    });
  }, [state.userCode, issueAsync, pushToast]);

  // 모달 열기/닫기 — 네이티브 `<dialog>`의 `showModal`/`close` 호출.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) {
      return;
    }
    if (restoreOpen && !dialog.open) {
      dialog.showModal();
    } else if (!restoreOpen && dialog.open) {
      dialog.close();
    }
  }, [restoreOpen]);

  /** 사용자 액션: 발급된 코드를 클립보드로 복사. */
  const handleCopy = async (): Promise<void> => {
    if (state.userCode === null) {
      return;
    }
    try {
      await navigator.clipboard.writeText(state.userCode);
      pushToast({ variant: 'success', message: '코드를 복사했어요.' });
    } catch {
      pushToast({
        variant: 'info',
        message: '복사에 실패했어요. 코드를 직접 메모해 주세요.',
      });
    }
  };

  /** 사용자 액션: 발급 실패 후 수동 재시도. */
  const handleRetry = (): void => {
    triggeredRef.current = true;
    issueAsync().catch((error: unknown) => {
      triggeredRef.current = false;
      const message =
        error instanceof ApiError
          ? error.message
          : '다시 시도해도 발급에 실패했어요. 잠시 후 시도해 주세요.';
      pushToast({ variant: 'error', message });
    });
  };

  const description = (
    <p className={styles.description}>
      다른 브라우저나 모바일에서 이어서 참여할 때 쓰는 본인 식별 코드예요.
    </p>
  );

  // === 1. 발급 완료 상태 ===
  if (state.userCode !== null) {
    return (
      <div className={styles.wrap}>
        <div className={styles.row} aria-live="polite">
          <span className={styles.label}>내 참여자 코드</span>
          <button
            type="button"
            className={styles.code}
            aria-label={`내 참여자 코드 ${state.userCode}, 클릭하여 복사`}
            onClick={() => {
              void handleCopy();
            }}
          >
            {state.userCode}
          </button>
          <span className={styles.divider} aria-hidden>
            ·
          </span>
          <button
            type="button"
            className={styles.link}
            onClick={() => {
              setRestoreOpen(true);
            }}
          >
            이어서 하기
          </button>
        </div>
        {description}

        <dialog
          ref={dialogRef}
          className={styles.dialog}
          onClose={() => {
            setRestoreOpen(false);
          }}
        >
          <button
            type="button"
            aria-label="닫기"
            className={styles.backdrop}
            onClick={() => {
              setRestoreOpen(false);
            }}
            tabIndex={-1}
          />
          <div className={styles.dialogInner}>
            <button
              type="button"
              className={styles.closeButton}
              aria-label="닫기"
              onClick={() => {
                setRestoreOpen(false);
              }}
            >
              ✕
            </button>
            <ParticipantOnboarding
              isReplacement
              onComplete={() => {
                setRestoreOpen(false);
              }}
            />
          </div>
        </dialog>
      </div>
    );
  }

  // === 2. 발급 실패 상태 ===
  if (issue.isError && !issue.isPending) {
    return (
      <div className={styles.wrap}>
        <div className={styles.row} role="status">
          <span className={`${styles.label} ${styles.labelError}`}>코드 발급 실패</span>
          <button type="button" className={styles.retry} onClick={handleRetry}>
            다시 시도
          </button>
        </div>
        {description}
      </div>
    );
  }

  // === 3. 발급 진행 중(또는 초기 진입) ===
  return (
    <div className={styles.wrap}>
      <div className={styles.row} role="status" aria-live="polite">
        <span className={styles.spinner} aria-hidden />
        <span className={styles.pendingLabel}>참여자 코드 발급 중…</span>
      </div>
      {description}
    </div>
  );
}
